import { Injectable, OnDestroy } from '@angular/core';
import { ActivationStart } from '@angular/router';

import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { GridSorting } from '@frontend/sports/grid/core/feature/model';
import { LocalStoreService } from '@frontend/vanilla/core';
import { isArray } from 'lodash-es';
import { Subject, takeUntil } from 'rxjs';

import { CompetitionRouteService } from '../../competition-list/competition-route.service';

export type SortingPage = 'live' | 'competitions' | 'coupons' | 'virtual';

@Injectable({ providedIn: 'root' })
export class SortingStateService implements OnDestroy {
    private config: Map<string, GridSorting>;
    private currentVirtualCompetitionId: number | null;
    private readonly storageKey = 'grid-sorting';
    private readonly serviceDestroyed$ = new Subject<void>();

    constructor(
        private storage: LocalStoreService,
        private routerEventsService: RouterEventsService,
        private competitionRouteService: CompetitionRouteService,
    ) {
        this.loadConfigFromStorage();
        this.subscribeVirtualCompetitionRouteChanges();
    }

    get(page: SortingPage, sport?: number): GridSorting | undefined {
        return this.getSorting(page, sport);
    }

    set(page: SortingPage, sorting: GridSorting, sport?: number): void {
        this.setSorting(page, sorting, sport);
        this.store();
    }

    getVirtualCompetition(): GridSorting {
        const virtualCompetition = this.getSorting('virtual');

        if (virtualCompetition === undefined) {
            return GridSorting.Time;
        }

        return virtualCompetition;
    }

    setByVirtualCompetitionId(sorting: GridSorting, competitionId: number): void {
        this.setSorting('virtual', sorting);
        this.currentVirtualCompetitionId = competitionId;
    }

    private removeVirtualCompetition(competitionIds?: number | number[]): void {
        if (!this.currentVirtualCompetitionId) {
            return;
        }

        if (isArray(competitionIds) || competitionIds !== this.currentVirtualCompetitionId) {
            this.currentVirtualCompetitionId = null;
            this.config.delete('virtual');
        }
    }

    private setSorting(page: SortingPage, sorting: GridSorting, sport?: number): void {
        const key = this.getKey(page, sport);
        this.config.set(key, sorting);
    }

    private getSorting(page: SortingPage, sport?: number): GridSorting | undefined {
        const key = this.getKey(page, sport);

        return this.config.get(key);
    }

    private getKey(page: SortingPage, sport?: number): string {
        if (page === 'virtual') {
            return page;
        }

        return [sport || 0, page].join('|');
    }

    private store(): void {
        this.storage.set(this.storageKey, Array.from(this.config.entries()));
    }

    private loadConfigFromStorage(): void {
        this.config = new Map(this.storage.get(this.storageKey) || []);
    }

    private subscribeVirtualCompetitionRouteChanges(): void {
        this.routerEventsService.currentActivationStart.pipe(takeUntil(this.serviceDestroyed$)).subscribe(this.tryRemoveVirtualCompetition);
    }

    // While in the context of a virtual competition, the sorting should not change when navigating
    // through the groups. Leaving the context of a given virtual competition, should reset the sorting
    private tryRemoveVirtualCompetition = (event?: ActivationStart): void => {
        if (event) {
            const competitionRouteParams = this.competitionRouteService.params();
            if (!competitionRouteParams.isVirtual || competitionRouteParams.league !== this.currentVirtualCompetitionId) {
                this.removeVirtualCompetition();
            }
        }
    };

    ngOnDestroy(): void {
        this.serviceDestroyed$.next();
        this.serviceDestroyed$.complete();
    }
}
